import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", answer1: "", answer2: "", answer3: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/members");
    axios.get(`${API}/registration-questions`).then(res => setQuestions(res.data.questions)).catch(() => {});
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: form.name, email: form.email, password: form.password,
        answer1: form.answer1, answer2: form.answer2, answer3: form.answer3,
      }, { withCredentials: true });
      if (res.data.token) localStorage.setItem("auth_token", res.data.token);
      window.location.href = "/members";
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="register-page" className="min-h-[60vh] flex items-center justify-center py-16">
      <div className="w-full max-w-md px-6">
        <div className="bg-white rounded-xl border border-[#E5E2DC] shadow-md p-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#C8A97E]/10 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-[#C8A97E]" />
            </div>
            <h1 className="font-serif text-2xl font-semibold text-[#1C2E4A]">Become a Member</h1>
          </div>
          {error && <div data-testid="register-error" className="bg-red-50 text-red-600 text-sm p-3 rounded-md mb-4 border border-red-200">{error}</div>}
          <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Full Name</Label>
              <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="border-[#E5E2DC] bg-[#FAF9F6]" required />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Email</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="border-[#E5E2DC] bg-[#FAF9F6]" required />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Password</Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="border-[#E5E2DC] bg-[#FAF9F6]" required />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Confirm Password</Label>
              <Input data-testid="register-confirm" type="password" value={form.confirmPassword} onChange={(e) => setForm({...form, confirmPassword: e.target.value})} className="border-[#E5E2DC] bg-[#FAF9F6]" required />
            </div>
            <div className="pt-2 border-t border-[#E5E2DC]">
              <p className="text-xs text-[#595959] mb-3">Please answer these questions to verify you are a real person:</p>
              {questions.map((q, i) => (
                <div key={q.id} className="mb-3">
                  <Label className="text-sm font-medium mb-1 block">{q.question}</Label>
                  <Input
                    data-testid={`register-answer-${i}`}
                    value={form[`answer${i+1}`]}
                    onChange={(e) => setForm({...form, [`answer${i+1}`]: e.target.value})}
                    className="border-[#E5E2DC] bg-[#FAF9F6]"
                    required
                  />
                </div>
              ))}
            </div>
            <button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full bg-[#1C2E4A] text-white hover:bg-[#152238] px-8 py-3 rounded-md transition-colors font-medium disabled:opacity-50">
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
